import React, { useState } from 'react';
import { X, Clock, DollarSign, TrendingUp, AlertTriangle, CheckCircle, Wallet } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function CloseShiftDialog({ open, onClose, shift, onShiftClosed }) {
  const [endingCash, setEndingCash] = useState('');
  const [displayCash, setDisplayCash] = useState('');
  const [notes, setNotes] = useState('');
  const [showHandover, setShowHandover] = useState(false);
  const [handoverTo, setHandoverTo] = useState('');
  const [handoverNotes, setHandoverNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);

  const formatNumber = (num) => {
    return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  };

  const handleCashInput = (value) => {
    // Remove all non-digit characters
    const rawValue = value.replace(/\D/g, '');
    setEndingCash(rawValue);
    
    // Format for display
    if (rawValue) {
      setDisplayCash(formatNumber(rawValue));
    } else {
      setDisplayCash('');
    }
  };

  React.useEffect(() => {
    if (open) {
      fetchUsers();
    }
  }, [open]);

  // Early return AFTER all hooks
  if (!open || !shift) return null;

  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/users`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.data.success) {
        const kasirUsers = response.data.users.filter(u => u.role === 'kasir');
        setUsers(kasirUsers);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const handleCloseShift = async () => {
    if (!endingCash || parseFloat(endingCash) < 0) {
      toast.error('Masukkan kas akhir yang valid');
      return;
    }

    if (showHandover && !handoverTo) {
      toast.error('Pilih kasir pengganti untuk serah terima');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/api/shifts/close`,
        {
          ending_cash: parseFloat(endingCash),
          notes: notes.trim(),
          handover_to: showHandover ? handoverTo : null,
          handover_notes: showHandover ? handoverNotes.trim() : null
        },
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      if (response.data.success) {
        toast.success('Shift berhasil ditutup!');
        onShiftClosed(response.data.shift);
        onClose();
      }
    } catch (error) {
      console.error('Error closing shift:', error);
      toast.error(error.response?.data?.detail || 'Gagal menutup shift');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0
    }).format(amount);
  };

  const formatDuration = (startTime) => {
    const start = new Date(startTime);
    const now = new Date();
    const diffMs = now - start;
    const hours = Math.floor(diffMs / (1000 * 60 * 60));
    const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
    return `${hours} jam ${minutes} menit`;
  };

  // Calculate expected cash and difference
  const startingCash = shift.starting_cash || 0;
  const totalRevenue = shift.total_revenue || 0;
  const cashRevenue = shift.cash_revenue || 0;
  const nonCashRevenue = shift.non_cash_revenue || 0;
  const cashTransactions = shift.cash_transactions || 0;
  const nonCashTransactions = shift.non_cash_transactions || 0;
  
  // Expected cash = starting cash + ONLY cash revenue (not including QRIS/Transfer)
  const expectedCash = startingCash + cashRevenue;
  const actualCash = parseFloat(endingCash) || 0;
  const cashDifference = actualCash - expectedCash;
  const isDifferenceOk = Math.abs(cashDifference) < 1000; // Toleransi Rp 1.000

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm overflow-y-auto py-4">
      <div className="bg-gradient-to-br from-slate-800 via-slate-800 to-slate-900 border-2 border-slate-700 rounded-2xl shadow-2xl w-full max-w-2xl mx-4 overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-orange-600 via-red-600 to-orange-600 px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-white/20 rounded-xl backdrop-blur-sm">
              <Clock className="h-6 w-6 text-white" />
            </div>
            <div>
              <h3 className="text-xl font-black text-white">Tutup Shift</h3>
              <p className="text-xs text-orange-200">Rekonsiliasi kas & serah terima</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-white/20 rounded-lg transition-colors"
          >
            <X className="h-5 w-5 text-white" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6 max-h-[70vh] overflow-y-auto scrollbar-hide">
          {/* Shift Summary */}
          <div className="bg-slate-900/50 border border-slate-700 rounded-xl p-5">
            <h4 className="text-white font-bold mb-4 flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-blue-400" />
              Ringkasan Shift
            </h4>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-xs text-white/60 mb-1">Durasi Shift</p>
                <p className="text-white font-bold">{formatDuration(shift.start_time)}</p>
              </div>
              <div>
                <p className="text-xs text-white/60 mb-1">Total Transaksi</p>
                <p className="text-white font-bold">{shift.total_transactions || 0} transaksi</p>
              </div>
              <div className="col-span-2">
                <p className="text-xs text-white/60 mb-1">Total Pendapatan</p>
                <p className="text-green-400 font-black text-xl">{formatCurrency(totalRevenue)}</p>
                <div className="flex gap-4 mt-2 text-xs">
                  <span className="text-white/70"> PS: {formatCurrency(shift.ps_revenue || 0)}</span>
                  <span className="text-white/70"> F&B: {formatCurrency(shift.fnb_revenue || 0)}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Payment Method Breakdown */}
          <div className="bg-slate-900/50 border border-slate-700 rounded-xl p-5">
            <h4 className="text-white font-bold mb-4 flex items-center gap-2">
              <Wallet className="h-5 w-5 text-purple-400" />
              Breakdown Metode Pembayaran
            </h4>
            
            <div className="space-y-3">
              <div className="flex justify-between items-center p-3 bg-green-500/10 border border-green-500/30 rounded-lg">
                <div>
                  <p className="text-green-400 font-bold"> Tunai (Cash)</p>
                  <p className="text-xs text-white/60">{cashTransactions} transaksi</p>
                </div>
                <p className="text-green-400 font-black text-lg">{formatCurrency(cashRevenue)}</p>
              </div>
              
              <div className="flex justify-between items-center p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                <div>
                  <p className="text-blue-400 font-bold"> Non-Tunai (QRIS/Transfer)</p>
                  <p className="text-xs text-white/60">{nonCashTransactions} transaksi</p>
                </div>
                <p className="text-blue-400 font-black text-lg">{formatCurrency(nonCashRevenue)}</p>
              </div>
            </div>
            
            <div className="mt-4 p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
              <p className="text-xs text-yellow-200/70 mb-1"> Catatan:</p>
              <p className="text-xs text-yellow-200">
                Non-tunai (QRIS/Transfer) <strong>tidak</strong> ada di laci fisik. 
                Hanya uang tunai yang harus dihitung di laci.
              </p>
            </div>
          </div>

          {/* Cash Reconciliation */}
          <div className="bg-slate-900/50 border border-slate-700 rounded-xl p-5">
            <h4 className="text-white font-bold mb-4 flex items-center gap-2">
              <DollarSign className="h-5 w-5 text-green-400" />
              Rekonsiliasi Kas
            </h4>
            
            <div className="space-y-3 mb-4">
              <div className="flex justify-between">
                <span className="text-white/70">Kas Awal:</span>
                <span className="text-white font-semibold">{formatCurrency(startingCash)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-white/70">+ Pendapatan Tunai:</span>
                <span className="text-green-400 font-semibold">{formatCurrency(cashRevenue)}</span>
              </div>
              <div className="border-t border-slate-600 pt-2 flex justify-between">
                <span className="text-white font-bold">Kas Seharusnya di Laci:</span>
                <span className="text-blue-400 font-black text-lg">{formatCurrency(expectedCash)}</span>
              </div>
            </div>

            {/* Ending Cash Input */}
            <div>
              <label className="block text-white text-sm font-bold mb-2">
                Kas Aktual (Hitung uang di laci)
              </label>
              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-white/60 font-semibold">
                  Rp
                </span>
                <input
                  type="text"
                  value={displayCash}
                  onChange={(e) => handleCashInput(e.target.value)}
                  placeholder="0"
                  className="w-full pl-12 pr-4 py-3 bg-slate-800 border-2 border-slate-600 rounded-xl text-white text-lg font-bold placeholder-white/30 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-all"
                  autoFocus
                />
              </div>
            </div>

            {/* Cash Difference Display */}
            {endingCash && (
              <div className={`mt-4 p-4 rounded-xl border-2 ${
                isDifferenceOk 
                  ? 'bg-green-500/10 border-green-500/30' 
                  : 'bg-red-500/10 border-red-500/30'
              }`}>
                <div className="flex items-start gap-3">
                  {isDifferenceOk ? (
                    <CheckCircle className="h-5 w-5 text-green-400 flex-shrink-0 mt-0.5" />
                  ) : (
                    <AlertTriangle className="h-5 w-5 text-red-400 flex-shrink-0 mt-0.5" />
                  )}
                  <div className="flex-1">
                    <p className={`font-bold text-sm mb-1 ${isDifferenceOk ? 'text-green-400' : 'text-red-400'}`}>
                      Selisih: {formatCurrency(Math.abs(cashDifference))}
                    </p>
                    <p className="text-xs text-white/70">
                      {cashDifference > 0 ? ' Kas lebih (surplus)' : cashDifference < 0 ? ' Kas kurang (minus)' : ' Kas pas'}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Notes */}
          <div>
            <label className="block text-white text-sm font-bold mb-2">
              Catatan Penutupan (opsional)
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Tambahkan catatan jika ada..."
              rows="3"
              className="w-full px-4 py-3 bg-slate-900/50 border-2 border-slate-600/50 rounded-xl text-white placeholder-white/30 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-all resize-none"
            />
          </div>

          {/* Handover Toggle */}
          <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-4">
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={showHandover}
                onChange={(e) => setShowHandover(e.target.checked)}
                className="w-5 h-5 rounded border-2 border-blue-400 bg-slate-700 checked:bg-blue-600 focus:ring-2 focus:ring-blue-500/20"
              />
              <span className="text-white font-semibold">Serah terima ke kasir pengganti</span>
            </label>
          </div>

          {/* Handover Section */}
          {showHandover && (
            <div className="bg-slate-900/50 border border-slate-700 rounded-xl p-5 space-y-4 animate-in fade-in slide-in-from-top-2 duration-300">
              <h4 className="text-white font-bold">Serah Terima</h4>
              
              <div>
                <label className="block text-white text-sm font-bold mb-2">
                  Kasir Pengganti
                </label>
                <select
                  value={handoverTo}
                  onChange={(e) => setHandoverTo(e.target.value)}
                  className="w-full px-4 py-3 bg-slate-800 border-2 border-slate-600 rounded-xl text-white focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-all"
                >
                  <option value="">Pilih kasir...</option>
                  {users.filter(u => u.user_id !== shift.user_id).map(user => (
                    <option key={user.user_id} value={user.user_id}>
                      {user.full_name} (@{user.username})
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-white text-sm font-bold mb-2">
                  Catatan Serah Terima
                </label>
                <textarea
                  value={handoverNotes}
                  onChange={(e) => setHandoverNotes(e.target.value)}
                  placeholder="Informasi penting untuk kasir pengganti..."
                  rows="3"
                  className="w-full px-4 py-3 bg-slate-800 border-2 border-slate-600 rounded-xl text-white placeholder-white/30 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-all resize-none"
                />
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="bg-slate-900/50 px-6 py-4 flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-3 bg-slate-700 hover:bg-slate-600 text-white font-bold rounded-xl transition-all"
          >
            Batal
          </button>
          <button
            onClick={handleCloseShift}
            disabled={loading || !endingCash || (showHandover && !handoverTo)}
            className="flex-1 px-4 py-3 bg-gradient-to-r from-red-600 to-orange-500 hover:from-red-700 hover:to-orange-600 disabled:from-gray-600 disabled:to-gray-600 text-white font-bold rounded-xl transition-all shadow-lg hover:shadow-red-500/50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {loading ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
                Menutup...
              </>
            ) : (
              <>
                <CheckCircle className="h-5 w-5" />
                Tutup Shift
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
